import { useEffect, useState } from 'react';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) || 'https://api.satutim.id';

export type ScenarioMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

export interface ScenarioStep {
    id: string;
    label: string;
    method: ScenarioMethod;
    path: string;
    body?: Record<string, unknown>;
    extract?: Record<string, string>;
}

export interface Scenario {
    id: string;
    title: string;
    description: string;
    steps: ScenarioStep[];
}

export interface ScenarioSummary {
    id: string;
    title: string;
    description: string;
    stepCount: number;
}

// Module-level cache — scenarios catalog is static, no point fetching
// twice within a tab session.
let listCache: ScenarioSummary[] | null = null;
const detailCache = new Map<string, Scenario>();

interface ListResult {
    scenarios: ScenarioSummary[];
    loading: boolean;
    error: string | null;
}

export function useScenarios(): ListResult {
    const [scenarios, setScenarios] = useState<ScenarioSummary[]>(listCache ?? []);
    const [loading, setLoading] = useState(!listCache);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (listCache) return;
        let cancelled = false;
        setLoading(true);
        fetch(`${API_BASE}/api/v1/scenarios`)
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then((payload: { data?: ScenarioSummary[] }) => {
                if (cancelled) return;
                const list = Array.isArray(payload?.data) ? payload.data : [];
                listCache = list;
                setScenarios(list);
            })
            .catch((e) => {
                if (!cancelled) setError(e?.message || 'Gagal memuat scenarios');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    return { scenarios, loading, error };
}

interface DetailResult {
    scenario: Scenario | null;
    loading: boolean;
    error: string | null;
}

export function useScenario(id: string | undefined): DetailResult {
    const [scenario, setScenario] = useState<Scenario | null>(id ? detailCache.get(id) ?? null : null);
    const [loading, setLoading] = useState(!!id && !detailCache.has(id));
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) {
            setScenario(null);
            setLoading(false);
            return;
        }
        const cached = detailCache.get(id);
        if (cached) {
            setScenario(cached);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        fetch(`${API_BASE}/api/v1/scenarios/${encodeURIComponent(id)}`)
            .then(async (r) => {
                if (r.status === 404) throw new Error('Scenario tidak ditemukan');
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then((payload: { data?: Scenario }) => {
                if (cancelled) return;
                if (!payload?.data) throw new Error('Response kosong');
                detailCache.set(id, payload.data);
                setScenario(payload.data);
            })
            .catch((e) => {
                if (!cancelled) setError(e?.message || 'Gagal memuat scenario');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [id]);

    return { scenario, loading, error };
}
